import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { ShieldCheck, ShieldX, Clock, RotateCcw, Ban, PlayCircle, Gavel, FileText, UserPlus, Info, Eye } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { QueuePager } from "@/components/admin/QueuePager";

function Btn({ onClick, disabled, tone = "slate", icon: Icon, children, testid }) {
  const tones = {
    green: "bg-[#16A34A] text-white hover:bg-green-700",
    red: "border border-red-200 text-red-600 hover:bg-red-50",
    blue: "bg-[#2563EB] text-white hover:bg-blue-700",
    slate: "border border-slate-200 text-slate-700 hover:bg-slate-50",
    amber: "border border-amber-300 text-amber-700 hover:bg-amber-50",
  };
  return (
    <button onClick={onClick} disabled={disabled} data-testid={testid}
      className={`h-9 px-3 rounded-md text-sm font-medium inline-flex items-center gap-1.5 disabled:opacity-60 ${tones[tone]}`}>
      {Icon && <Icon size={15} />} {children}
    </button>
  );
}

export default function ComplianceTab() {
  const [queue, setQueue] = useState([]);
  const [filter, setFilter] = useState("");
  const [overrides, setOverrides] = useState(null);
  const [caseData, setCaseData] = useState(null);
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState("");
  const [ov, setOv] = useState({ user_id: "", new_result: "verified", reason: "", evidence: "", password: "", high_risk: false, risk_flag: "" });

  const [page, setPage] = useState({ total: 0, hasMore: false, loading: false });
  const loadQueue = useCallback((append = false, offset = 0) => {
    setPage((p) => ({ ...p, loading: true }));
    api.get("/admin/verification/queue", { params: { status: filter || undefined, skip: append ? offset : 0, limit: 100 } })
      .then(({ data }) => {
        setQueue((prev) => (append ? [...prev, ...(data.queue || [])] : data.queue || []));
        setPage({ total: data.total ?? (data.queue || []).length, hasMore: !!data.has_more, loading: false });
      }).catch(() => setPage((p) => ({ ...p, loading: false })));
  }, [filter]);
  const load = useCallback(() => {
    loadQueue();
    api.get("/admin/verification/overrides").then(({ data }) => setOverrides(data)).catch(() => {});
  }, [loadQueue]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, key, okMsg) => {
    setBusy(key);
    try { await fn(); toast.success(okMsg); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const openCase = (uid) => {
    api.get(`/admin/verification/case/${uid}`).then(({ data }) => setCaseData(data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
    api.get(`/admin/verification/documents/${uid}`).then(({ data }) => setDocs(data.documents || [])).catch(() => setDocs([]));
  };

  const decide = (uid, approved) => act(() => api.post("/admin/verification/decide",
    { user_id: uid, approved, reason: approved ? "Approved after review" : "Rejected — insufficient evidence" }), uid + approved, approved ? "Verified" : "Rejected");
  const extend = (uid) => act(() => api.post("/admin/verification/extend", { user_id: uid, days: 3 }), uid + "e", "Deadline +3 days");
  const restrict = (uid) => act(() => api.post("/admin/verification/restrict", { user_id: uid, reason: "Manual restriction" }), uid + "r", "Restricted");
  const restore = (uid) => act(() => api.post("/admin/verification/restore", { user_id: uid, reason: "Manual restore" }), uid + "s", "Restored");
  const requestInfo = (uid) => act(() => api.post("/admin/verification/request-info", { user_id: uid, message: "Please upload incorporation + EIN documents." }), uid + "i", "Info requested");
  const assign = (uid) => { const rev = prompt("Reviewer user id:"); if (rev) act(() => api.post("/admin/verification/assign", { user_id: uid, reviewer_user_id: rev }), uid + "a", "Reviewer assigned"); };
  const runEnforcement = () => act(() => api.post("/admin/verification/run-enforcement"), "enf", "Enforcement ran");
  const secondApprove = (oid) => { const pw = prompt("Re-enter your password to co-approve:"); if (pw) act(() => api.post("/admin/verification/override/second-approve", { override_id: oid, password: pw }), oid, "Dual approval complete"); };

  const submitOverride = (e) => { e.preventDefault(); act(() => api.post("/admin/verification/override", ov), "ov", "Override submitted"); };
  const setOvF = (k) => (e) => setOv((s) => ({ ...s, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  return (
    <div className="space-y-8" data-testid="admin-compliance-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-heading text-base font-bold text-[#0A0F1C]">Verification enforcement</h3>
          <p className="text-xs text-slate-500">Backend-enforced by role. Actions you lack permission for will be rejected.</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="queue-filter" className="h-9 rounded-md border border-slate-300 px-2 text-sm">
            <option value="">All statuses</option>
            <option value="manual_review">manual_review</option>
            <option value="info_requested">info_requested</option>
            <option value="approved">approved</option>
            <option value="rejected">rejected</option>
          </select>
          <Btn onClick={runEnforcement} disabled={busy === "enf"} tone="amber" icon={PlayCircle} testid="run-enforcement-btn">Run enforcement</Btn>
        </div>
      </div>

      <div className="space-y-3" data-testid="verification-queue">
        <QueuePager shown={queue.length} total={page.total} hasMore={page.hasMore} loading={page.loading}
          onMore={() => loadQueue(true, queue.length)} testid="verification-pager" />
        {queue.length === 0 ? (
          <div className="border border-dashed border-slate-200 bg-white rounded-lg p-10 text-center text-slate-400">No submissions in this view.</div>
        ) : queue.map((r) => (
          <div key={r.user_id} className="border border-slate-200 bg-white rounded-lg p-4 flex flex-wrap items-center gap-2" data-testid={`vq-row-${r.user_id}`}>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-[#0A0F1C]">{r.legal_name || r.user_id}</div>
              <div className="text-xs text-slate-500">{r.jurisdiction} · status {r.status} {r.document_count != null && `· ${r.document_count} docs`}</div>
            </div>
            <Btn onClick={() => openCase(r.user_id)} icon={Eye} testid={`vq-open-${r.user_id}`}>Open case</Btn>
            <Btn onClick={() => decide(r.user_id, true)} disabled={busy === r.user_id + true} tone="green" icon={ShieldCheck} testid={`vq-approve-${r.user_id}`}>Approve</Btn>
            <Btn onClick={() => decide(r.user_id, false)} disabled={busy === r.user_id + false} tone="red" icon={ShieldX} testid={`vq-reject-${r.user_id}`}>Reject</Btn>
            <Btn onClick={() => extend(r.user_id)} icon={Clock} testid={`vq-extend-${r.user_id}`}>+3d</Btn>
            <Btn onClick={() => requestInfo(r.user_id)} icon={Info} testid={`vq-info-${r.user_id}`}>Request info</Btn>
            <Btn onClick={() => assign(r.user_id)} icon={UserPlus} testid={`vq-assign-${r.user_id}`}>Assign</Btn>
            <Btn onClick={() => restrict(r.user_id)} icon={Ban} testid={`vq-restrict-${r.user_id}`}>Restrict</Btn>
            <Btn onClick={() => restore(r.user_id)} icon={RotateCcw} testid={`vq-restore-${r.user_id}`}>Restore</Btn>
          </div>
        ))}
      </div>

      {caseData && (
        <div className="border border-slate-200 bg-white rounded-lg p-5" data-testid="case-detail">
          <div className="flex items-center justify-between">
            <h3 className="font-heading text-base font-bold text-[#0A0F1C]">Case · {caseData.business?.legal_name || caseData.user_id}</h3>
            <span className="rounded-full bg-slate-900 px-2.5 py-0.5 text-xs font-semibold uppercase text-white">{caseData.verification_state}</span>
          </div>
          <p className="mt-1 text-xs text-slate-400">Reg#: {caseData.business?.registration_number || "—"} (masked) · {caseData.email}</p>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <h4 className="text-xs font-semibold uppercase text-slate-400 mb-1">Components</h4>
              <ul className="text-sm space-y-0.5">
                {Object.entries(caseData.components || {}).map(([k, v]) => (
                  <li key={k} className="flex justify-between"><span className="text-slate-600">{k.replace(/_/g, " ")}</span><span className="font-medium">{v}</span></li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-xs font-semibold uppercase text-slate-400 mb-1">Agency adapter results</h4>
              <ul className="text-sm space-y-0.5">
                {(caseData.agency_results || []).map((a, i) => (
                  <li key={i} className="flex justify-between"><span className="text-slate-600">{a.agency}</span><span className="font-medium">{a.status}</span></li>
                ))}
                {(!caseData.agency_results || caseData.agency_results.length === 0) && <li className="text-slate-400">No submission yet.</li>}
              </ul>
            </div>
          </div>
          <div className="mt-4">
            <h4 className="text-xs font-semibold uppercase text-slate-400 mb-1 flex items-center gap-1"><FileText size={13} /> Protected documents (audited)</h4>
            <ul className="text-sm space-y-0.5">
              {docs.map((d, i) => <li key={i}>{d.filename} <span className="text-slate-400">({d.doc_type}) · link expires {d.access_expires_in}</span></li>)}
              {docs.length === 0 && <li className="text-slate-400">No documents.</li>}
            </ul>
          </div>
          <div className="mt-4">
            <h4 className="text-xs font-semibold uppercase text-slate-400 mb-1">Verification history</h4>
            <ul className="text-xs text-slate-500 space-y-0.5 max-h-40 overflow-y-auto">
              {(caseData.history || []).map((e, i) => <li key={i}>{new Date(e.created_at).toLocaleString()} — <strong>{e.action}</strong> by {e.actor_role || "system"}</li>)}
            </ul>
          </div>
        </div>
      )}

      {overrides?.pending_dual_approvals?.length > 0 && (
        <div className="border border-amber-300 bg-amber-50 rounded-lg p-5" data-testid="pending-dual-approvals">
          <h3 className="font-heading text-base font-bold text-amber-900">Pending dual approvals</h3>
          {overrides.pending_dual_approvals.map((o) => (
            <div key={o.override_id} className="mt-2 flex items-center justify-between text-sm">
              <span>{o.target_user_id}: → <strong>{o.new_result}</strong> ({o.risk_flag})</span>
              <Btn onClick={() => secondApprove(o.override_id)} tone="green" testid={`second-approve-${o.override_id}`}>Co-approve</Btn>
            </div>
          ))}
        </div>
      )}

      <div className="border border-slate-200 bg-white rounded-lg p-5">
        <h3 className="font-heading text-base font-bold text-[#0A0F1C] flex items-center gap-2"><Gavel size={16} /> Verification override <span className="text-xs font-normal text-slate-400">(Senior Manager / Compliance Director / Super Admin)</span></h3>
        <form onSubmit={submitOverride} className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="override-form">
          <input placeholder="Target user id" value={ov.user_id} onChange={setOvF("user_id")} required data-testid="ov-user-id" className="rounded-md border border-slate-300 px-3 py-2 text-sm" />
          <select value={ov.new_result} onChange={setOvF("new_result")} data-testid="ov-result" className="rounded-md border border-slate-300 px-3 py-2 text-sm">
            <option value="verified">verified</option>
            <option value="verification_restricted">verification_restricted</option>
            <option value="verification_review_required">verification_review_required</option>
            <option value="rejected">rejected</option>
          </select>
          <input placeholder="Reason (required)" value={ov.reason} onChange={setOvF("reason")} required data-testid="ov-reason" className="rounded-md border border-slate-300 px-3 py-2 text-sm" />
          <input placeholder="Evidence / reference (required)" value={ov.evidence} onChange={setOvF("evidence")} required data-testid="ov-evidence" className="rounded-md border border-slate-300 px-3 py-2 text-sm" />
          <input type="password" placeholder="Re-enter your password" value={ov.password} onChange={setOvF("password")} required data-testid="ov-password" className="rounded-md border border-slate-300 px-3 py-2 text-sm" />
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input type="checkbox" checked={ov.high_risk} onChange={setOvF("high_risk")} data-testid="ov-highrisk" /> High-risk (requires 2nd approver)
          </label>
          <div className="sm:col-span-2 flex justify-end">
            <button type="submit" data-testid="ov-submit" className="h-9 px-4 rounded-md bg-[#2563EB] text-white text-sm font-semibold">Submit override</button>
          </div>
        </form>
      </div>

      {overrides && (
        <div className="border border-slate-200 bg-white rounded-lg p-5" data-testid="overrides-dashboard">
          <h3 className="font-heading text-base font-bold text-[#0A0F1C]">Compliance override log</h3>
          <div className="mt-2 flex gap-6 text-sm text-slate-600">
            <span>Total: <strong>{overrides.total}</strong></span>
            <span>Pending: <strong className="text-amber-700">{overrides.pending_dual_approvals?.length || 0}</strong></span>
            <span>Reversed: <strong>{overrides.reversed?.length || 0}</strong></span>
          </div>
        </div>
      )}
    </div>
  );
}
